import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import * as walletService from '../../services/walletService'
import {
  clearAffiliateMeCache,
  getAffiliateMe,
  affiliateWithdrawRequest,
} from '../../services/affiliateService'
import type { AffiliateMeData } from '../../services/affiliateService'
import {
  affiliateWithdrawProgress,
  affiliateWithdrawShortfall,
  canAffiliateCommissionWithdraw,
  resolveMinCommissionWithdraw,
} from './affiliateConstants'
import { affiliateCardShell, affiliateMutedText, affiliateSubtleText } from './affiliateUi'
import './affiliateLayout.css'

const AffiliateWithdrawalPage: React.FC = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [affiliateData, setAffiliateData] = useState<AffiliateMeData | null>(null)
  const [amount, setAmount] = useState('')

  const loadAffiliateData = async (forceRefresh = false) => {
    if (forceRefresh) {
      clearAffiliateMeCache()
    }
    setLoading(true)
    setError(null)
    try {
      const me = await getAffiliateMe(forceRefresh)
      setAffiliateData(me)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadAffiliateData(true)
  }, [])

  useEffect(() => {
    const refreshFromServer = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        return
      }
      void loadAffiliateData(true)
    }

    window.addEventListener('focus', refreshFromServer)
    document.addEventListener('visibilitychange', refreshFromServer)

    return () => {
      window.removeEventListener('focus', refreshFromServer)
      document.removeEventListener('visibilitychange', refreshFromServer)
    }
  }, [])

  const remaining = affiliateData?.remainingEarnings ?? 0
  const withdrawRequested = affiliateData?.payoutSummary?.withdrawRequested ?? 0
  const minCommissionWithdraw = resolveMinCommissionWithdraw(
    affiliateData?.minCommissionWithdrawalAmount,
  )
  const canWithdraw = canAffiliateCommissionWithdraw(remaining, minCommissionWithdraw)
  const shortfall = affiliateWithdrawShortfall(remaining, minCommissionWithdraw)
  const progressPct = affiliateWithdrawProgress(remaining, minCommissionWithdraw)

  const payoutMode = useMemo(() => {
    const userMode = affiliateData?.userPayoutMode
    if (userMode && userMode !== 'inherit') return userMode
    return affiliateData?.payoutMode || 'manual'
  }, [affiliateData?.payoutMode, affiliateData?.userPayoutMode])

  const payoutHint =
    payoutMode === 'auto'
      ? 'เมื่อส่งคำขอ ระบบจะโอนค่าคอมมิชชั่นเข้าเครดิตทันที จากนั้นคุณสามารถถอนเงินธนาคารได้ตามปกติ'
      : 'เมื่อส่งคำขอ ระบบจะล็อกยอดและรอแอดมินอนุมัติ จากนั้นค่าคอมมิชชั่นจะเข้าเครดิตเพื่อถอนเงินธนาคาร'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const num = parseFloat(amount.replace(/,/g, ''))
    if (Number.isNaN(num) || num < minCommissionWithdraw) {
      Swal.fire({
        icon: 'warning',
        title: 'จำนวนไม่ถูกต้อง',
        text: `กรุณาระบุจำนวนขั้นต่ำ ${minCommissionWithdraw} บาท`,
      })
      return
    }
    if (num > remaining) {
      Swal.fire({
        icon: 'warning',
        title: 'ยอดไม่เพียงพอ',
        text: `รายได้คงเหลือ ${walletService.formatCurrency(remaining)}`,
      })
      return
    }
    setSubmitting(true)
    try {
      const res = await affiliateWithdrawRequest(num)
      if (res.success && res.pendingApproval) {
        Swal.fire({
          icon: 'info',
          title: 'แจ้งเตือน',
          text: res.message || 'รออนุมัติ',
        }).then(() => {
          setAmount('')
          navigate('/affiliate/overview')
        })
      } else if (res.success) {
        Swal.fire({
          icon: 'success',
          title: 'สำเร็จ',
          text: res.message || 'ค่าคอมมิชชั่นเข้าเครดิตแล้ว',
        }).then(() => {
          setAmount('')
          navigate('/affiliate/overview')
        })
      } else {
        Swal.fire({
          icon: 'info',
          title: 'แจ้งเตือน',
          text: (res as { message?: string }).message || 'ไม่สามารถดำเนินการได้ในขณะนี้',
        })
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'เกิดข้อผิดพลาด'
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: msg,
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleWithdrawAll = () => {
    const whole = Math.floor(remaining)
    setAmount(String(whole))
  }

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" role="status" />
        <p className="mt-2 c_tertiary">กำลังโหลดข้อมูล...</p>
      </div>
    )
  }
  if (error) {
    return (
      <div className="text-center py-4">
        <p className="c_primary mb-2">{error}</p>
        <Link to="/affiliate/overview" className="link_pmr btn border_30 border px-3 py-2 c_tertiary">
          กลับไปแนะนำเพื่อน
        </Link>
      </div>
    )
  }

  return (
    <div className="row mx__1">
      <div className="col-12 px-1 mb-3">
        <div className="light_sd p-3 p-md-4 overflow-hidden" style={affiliateCardShell}>
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3">
            <div>
              <div
                className="d-inline-flex align-items-center px-3 py-1 mb-2"
                style={{
                  borderRadius: 999,
                  background: 'rgba(86, 144, 255, 0.18)',
                  border: '1px solid rgba(126, 172, 255, 0.25)',
                }}
              >
                <span className="c_tertiary small fw-semibold">Commission Wallet</span>
              </div>
              <h4 className="mb-1 c_tertiary">รายได้คงเหลือ</h4>
              <p className="mb-0 small" style={{ color: affiliateMutedText }}>
                ยอดที่พร้อมส่งคำขอถอนคอมมิชชั่น
              </p>
            </div>
            <div
              className="px-4 py-3 text-center text-md-end"
              style={{
                minWidth: 220,
                borderRadius: 18,
                background: 'rgba(15, 29, 71, 0.78)',
                border: '1px solid rgba(121, 172, 255, 0.18)',
              }}
            >
              <div className="small mb-1" style={{ color: affiliateSubtleText }}>ยอดคงเหลือ (บาท)</div>
              <div className="fw-bold c_primary" style={{ fontSize: '2rem', lineHeight: 1.1 }}>
                {walletService.formatCurrency(remaining)}
              </div>
              {withdrawRequested > 0 && (
                <div className="small mt-2" style={{ color: '#fbbf24' }}>
                  รออนุมัติ {walletService.formatCurrency(withdrawRequested)} บาท
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="col-12 px-1 mb-3">
        <div
          className="p-3 p-md-4 overflow-hidden"
          style={{
            borderRadius: 20,
            border: canWithdraw
              ? '1px solid rgba(49, 208, 126, 0.35)'
              : '1px solid rgba(251, 191, 36, 0.35)',
            background: canWithdraw
              ? 'linear-gradient(160deg, rgba(12, 48, 38, 0.92), rgba(8, 18, 46, 0.94))'
              : 'linear-gradient(160deg, rgba(58, 38, 8, 0.92), rgba(8, 18, 46, 0.94))',
          }}
        >
          <div className="d-flex align-items-start gap-3 mb-3">
            <div
              className="d-flex align-items-center justify-content-center flex-shrink-0"
              style={{
                width: 44,
                height: 44,
                borderRadius: 14,
                background: canWithdraw ? 'rgba(49, 208, 126, 0.18)' : 'rgba(251, 191, 36, 0.18)',
              }}
            >
              <i
                className={`fas ${canWithdraw ? 'fa-check-circle' : 'fa-hourglass-half'}`}
                style={{ color: canWithdraw ? '#4ade80' : '#fbbf24', fontSize: '1.1rem' }}
              />
            </div>
            <div>
              <h5 className="c_tertiary mb-1">
                {canWithdraw ? 'พร้อมส่งคำขอถอน' : 'ยังถอนไม่ได้ — ยอดยังไม่ถึงขั้นต่ำ'}
              </h5>
              <p className="mb-0 small" style={{ color: affiliateMutedText }}>
                {canWithdraw
                  ? `ถอนได้ขั้นต่ำ ${minCommissionWithdraw} บาท และไม่เกินยอดคงเหลือ`
                  : `ต้องมีรายได้คงเหลืออย่างน้อย ${minCommissionWithdraw} บาท (ขาดอีก ${walletService.formatCurrency(shortfall)} บาท)`}
              </p>
            </div>
          </div>

          <div className="mb-2 d-flex justify-content-between small" style={{ color: affiliateSubtleText }}>
            <span>ความคืบหน้าสู่ขั้นต่ำ {minCommissionWithdraw} บาท</span>
            <span>{progressPct.toFixed(0)}%</span>
          </div>
          <div
            className="mb-3"
            style={{
              height: 10,
              borderRadius: 999,
              background: 'rgba(15, 29, 71, 0.9)',
              overflow: 'hidden',
              border: '1px solid rgba(121, 172, 255, 0.12)',
            }}
          >
            <div
              style={{
                width: `${progressPct}%`,
                height: '100%',
                borderRadius: 999,
                background: canWithdraw
                  ? 'linear-gradient(90deg, #31d07f 0%, #48a9ff 100%)'
                  : 'linear-gradient(90deg, #f59e0b 0%, #fbbf24 100%)',
                transition: 'width 0.35s ease',
              }}
            />
          </div>

          {!canWithdraw ? (
            <div
              className="p-3"
              style={{
                borderRadius: 16,
                background: 'rgba(15, 29, 71, 0.72)',
                border: '1px solid rgba(121, 172, 255, 0.14)',
              }}
            >
              <div className="c_tertiary fw-semibold mb-2">
                <i className="fas fa-lightbulb me-2" style={{ color: '#fbbf24' }} />
                วิธีเพิ่มรายได้คอมมิชชั่น
              </div>
              <ul className="mb-0 ps-3 small" style={{ color: affiliateMutedText }}>
                <li className="mb-1">แชร์ลิงก์แนะนำเพื่อนจากหน้าภาพรวม</li>
                <li className="mb-1">รับคอมมิชชั่นเมื่อสมาชิกที่แนะนำเล่นและรอบ/เกมสรุปผลแล้ว</li>
                <li>เมื่อยอดถึง {minCommissionWithdraw} บาท ปุ่มส่งคำขอถอนจะเปิดให้ใช้งานทันที</li>
              </ul>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <label className="form-label c_tertiary mb-2">จำนวนเงินที่ต้องการถอน (บาท)</label>
              <div className="d-flex flex-column flex-sm-row gap-2 mb-3">
                <input
                  type="text"
                  inputMode="numeric"
                  className="form-control flex-grow-1"
                  placeholder={`ขั้นต่ำ ${minCommissionWithdraw} บาท`}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/\D/g, ''))}
                  style={{
                    borderRadius: 14,
                    border: '1px solid rgba(121, 172, 255, 0.28)',
                    background: 'rgba(8, 18, 46, 0.95)',
                    color: '#f8fafc',
                    padding: '0.75rem 1rem',
                  }}
                />
                <button
                  type="button"
                  className="btn border-0 flex-shrink-0 affiliate-withdraw-all-btn"
                  onClick={handleWithdrawAll}
                  style={{
                    borderRadius: 14,
                    background: 'rgba(86, 144, 255, 0.22)',
                    border: '1px solid rgba(126, 172, 255, 0.28)',
                    minWidth: 120,
                  }}
                >
                  ถอนทั้งหมด
                </button>
              </div>

              <p className="small mb-3" style={{ color: affiliateSubtleText }}>
                <i className="fas fa-info-circle me-1" />
                {payoutHint}
              </p>

              <button
                type="submit"
                className="link_pmr w-100 box_row justify-content-center font_rg border-0 py-3 c_tertiary"
                disabled={submitting || !amount}
                style={{
                  borderRadius: 18,
                  background: submitting || !amount
                    ? 'linear-gradient(90deg, #6b7280 0%, #94a3b8 100%)'
                    : 'linear-gradient(90deg, #31d07f 0%, #48a9ff 100%)',
                  boxShadow: submitting || !amount
                    ? 'none'
                    : '0 12px 28px rgba(46, 208, 126, 0.18)',
                  opacity: submitting || !amount ? 0.7 : 1,
                }}
              >
                {submitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                    กำลังส่งคำขอ...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane me-2" />
                    ส่งคำขอถอน
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>

      <div className="col-12 px-1 mb-3">
        <div
          className="p-3"
          style={{
            borderRadius: 18,
            border: '1px solid rgba(91, 154, 255, 0.22)',
            background: 'rgba(8, 18, 46, 0.88)',
          }}
        >
          <div className="c_tertiary fw-semibold mb-3">ขั้นตอนการถอนคอมมิชชั่น</div>
          <div className="row g-2">
            {[
              { step: '1', title: 'สะสมรายได้', desc: 'รับคอมจากสมาชิกที่แนะนำ' },
              { step: '2', title: 'ส่งคำขอถอน', desc: `ยอดขั้นต่ำ ${minCommissionWithdraw} บาท` },
              { step: '3', title: payoutMode === 'auto' ? 'เข้าเครดิตอัตโนมัติ' : 'รออนุมัติ', desc: payoutMode === 'auto' ? 'ระบบโอนเข้าเครดิตทันที' : 'แอดมินตรวจสอบคำขอ' },
              { step: '4', title: 'ถอนเงินธนาคาร', desc: 'ถอนจากเมนู Wallet ตามปกติ' },
            ].map((item) => (
              <div key={item.step} className="col-6 col-md-3">
                <div
                  className="h-100 p-2 p-md-3"
                  style={{
                    borderRadius: 14,
                    background: 'rgba(15, 29, 71, 0.72)',
                    border: '1px solid rgba(121, 172, 255, 0.12)',
                  }}
                >
                  <div
                    className="d-inline-flex align-items-center justify-content-center mb-2 fw-bold"
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 999,
                      background: 'rgba(86, 144, 255, 0.22)',
                      color: '#93c5fd',
                      fontSize: '0.85rem',
                    }}
                  >
                    {item.step}
                  </div>
                  <div className="c_tertiary small fw-semibold mb-1">{item.title}</div>
                  <div className="small" style={{ color: '#94a3b8' }}>{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="col-12 px-1">
        <Link
          to="/affiliate/overview"
          className="link_pmr w-100 box_row justify-content-center font_rg border_30 border py-3 text-decoration-none c_tertiary"
          style={{
            borderRadius: 18,
            background: 'rgba(220, 38, 38, 0.12)',
            borderColor: 'rgba(248, 113, 113, 0.35) !important',
          }}
        >
          <i className="fas fa-arrow-left me-2" />
          กลับไปแนะนำเพื่อน
        </Link>
      </div>
    </div>
  )
}

export default AffiliateWithdrawalPage
